/**
 * Request Form Component
 *
 * Expenditure request creation form with validation and tooltips.
 * Features:
 * - Title and description text inputs
 * - Expense type dropdown
 * - Amount input with currency selector
 * - Frequency selector
 * - Form validation with inline errors
 * - Contextual tooltips for each field
 * - Save as Draft and Submit buttons with confirmation
 * - Dark theme styling with glass morphism
 */

import { Input } from './input';
import { Select } from './select';
import { createTextarea } from './textarea';
import { FormField } from './form-field';
import { confirm } from './modal';
import { renderIcon } from '../utils/icons';
import type { ValidationState, SelectOption } from '../types/form';
import { apiClient } from '../services/api-client';
import { FileUpload, type UploadedFile } from './file-upload';
import { convertCurrencyLocal, formatCurrencyAmount } from '../services/currency-service';

/**
 * Expense type options based on app specification
 */
export const EXPENSE_TYPES: SelectOption[] = [
  { value: 'OFFICE_SUPPLIES', label: 'Office Material' },
  { value: 'TRAINING', label: 'External Event/Conference' },
  { value: 'OTHER', label: 'Internal Company Event/Workshop' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'EQUIPMENT', label: 'Out-of-Pocket Expense' },
  { value: 'PROFESSIONAL_SERVICES', label: 'Other' },
];

/**
 * Frequency options for expenditure requests
 */
export const FREQUENCY_OPTIONS: SelectOption[] = [
  { value: 'ONE_TIME', label: 'One Time' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'ANNUAL', label: 'Annual' },
];

/**
 * Currency interface (from backend)
 */
export interface Currency {
  code: string;
  name: string;
  symbol: string;
  isActive: boolean;
  isDefault: boolean;
}

/**
 * Queued file for upload after request creation
 */
export interface QueuedFile {
  /** The actual file object */
  file: File;
  /** Local ID for tracking */
  localId: string;
  /** Original filename */
  name: string;
  /** File size in bytes */
  size: number;
  /** MIME type */
  type: string;
}

/**
 * Form data interface
 */
export interface RequestFormData {
  title: string;
  description: string;
  expenseType: string;
  amount: string;
  currency: string;
  frequency: string;
  /** Queued files to be uploaded after request creation */
  queuedFiles?: QueuedFile[];
}

/**
 * Field tooltips for contextual help
 */
const FIELD_TOOLTIPS = {
  title: 'Enter a brief, descriptive title for your expenditure request. This will be visible in all request lists.',
  description: 'Provide a detailed explanation of the expenditure, including the purpose and expected benefits.',
  expenseType: 'Select the category that best describes this expenditure. This helps with reporting and routing.',
  amount: 'Enter the total amount requested. Use numbers only (e.g., 1500.00).',
  currency: 'Select the currency for this expenditure. Approval thresholds may vary by currency.',
  frequency: 'Indicate if this is a one-time expense or recurring. Recurring expenses require additional approval.',
  attachments: 'Upload supporting documents such as quotes, invoices, or receipts. Files will be attached after the request is created.',
};

/**
 * Validation error messages
 */
const VALIDATION_MESSAGES = {
  title: {
    required: 'Title is required',
    minLength: 'Title must be at least 3 characters',
    maxLength: 'Title must be less than 200 characters',
  },
  description: {
    required: 'Description is required',
    minLength: 'Description must be at least 10 characters',
    maxLength: 'Description must be less than 2000 characters',
  },
  expenseType: {
    required: 'Please select an expense type',
  },
  amount: {
    required: 'Amount is required',
    invalid: 'Please enter a valid amount',
    positive: 'Amount must be greater than 0',
  },
  currency: {
    required: 'Please select a currency',
  },
  frequency: {
    required: 'Please select a frequency',
  },
};

/**
 * Request form component options
 */
export interface RequestFormOptions {
  /** Parent container element */
  container: HTMLElement;
  /** Callback when form is submitted (not draft) */
  onSubmit?: (data: RequestFormData) => void | Promise<void>;
  /** Callback when draft is saved */
  onSaveDraft?: (data: RequestFormData) => void | Promise<void>;
  /** Callback when form is cancelled */
  onCancel?: () => void;
  /** Initial form data (for editing) */
  initialData?: Partial<RequestFormData>;
  /** Whether form is in edit mode */
  isEditMode?: boolean;
}

/**
 * Request Form Component Class
 */
export class RequestForm {
  private container: HTMLElement;
  private formElement: HTMLFormElement | null = null;
  private options: RequestFormOptions;

  // Form fields
  private titleField: FormField | null = null;
  private descriptionField: FormField | null = null;
  private expenseTypeField: FormField | null = null;
  private amountField: FormField | null = null;
  private currencyField: FormField | null = null;
  private frequencyField: FormField | null = null;
  private conversionPreview: HTMLElement | null = null;
  private attachmentsField: HTMLElement | null = null;

  // Input elements
  private titleInput: Input | null = null;
  private descriptionTextarea: HTMLTextAreaElement | null = null;
  private expenseTypeSelect: Select | null = null;
  private amountInput: Input | null = null;
  private currencySelect: Select | null = null;
  private frequencySelect: Select | null = null;
  private fileUpload: FileUpload | null = null;

  // Queued files for upload (stored separately from FileUpload's internal state)
  private queuedFiles: Map<string, File> = new Map();

  // State
  private currencies: Currency[] = [];
  private formData: RequestFormData = {
    title: '',
    description: '',
    expenseType: '',
    amount: '',
    currency: '',
    frequency: 'ONE_TIME',
  };
  private errors: Partial<Record<keyof RequestFormData, string>> = {};

  constructor(options: RequestFormOptions) {
    this.container = options.container;
    this.options = options;

    // Initialize with any provided data
    if (options.initialData) {
      this.formData = { ...this.formData, ...options.initialData };
    }
  }

  /**
   * Initialize and render the form
   */
  async init(): Promise<void> {
    // Load currencies first
    await this.loadCurrencies();

    // Render the form
    this.render();

    // Set up event listeners
    this.attachEventListeners();

    // Initial conversion preview update (if editing existing request)
    if (this.formData.amount && this.formData.currency) {
      this.updateConversionPreview();
    }
  }

  /**
   * Load currencies from the API
   */
  private async loadCurrencies(): Promise<void> {
    try {
      const response = await apiClient.get<{ data: Currency[] }>('/admin/currencies?activeOnly=true');
      this.currencies = response.data.data || [];

      // Set default currency if not already set
      if (!this.formData.currency && this.currencies.length > 0) {
        const defaultCurrency = this.currencies.find(c => c.isDefault);
        this.formData.currency = defaultCurrency?.code || this.currencies[0]?.code || '';
      }
    } catch (error) {
      console.error('Failed to load currencies:', error);
      // Use fallback currencies
      this.currencies = [
        { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', isActive: true, isDefault: true },
        { code: 'EUR', name: 'Euro', symbol: '€', isActive: true, isDefault: false },
        { code: 'USD', name: 'US Dollar', symbol: '$', isActive: true, isDefault: false },
      ];
      if (!this.formData.currency) {
        this.formData.currency = 'CHF';
      }
    }
  }

  /**
   * Render the form
   */
  private render(): void {
    this.container.innerHTML = '';
    this.container.className = 'request-form-container';

    // Create main card wrapper
    const card = document.createElement('div');
    card.className = 'glass-card p-6 md:p-8 rounded-2xl';

    // Header
    const header = this.createHeader();
    card.appendChild(header);

    // Form element
    this.formElement = document.createElement('form');
    this.formElement.className = 'space-y-6 mt-6';
    this.formElement.noValidate = true;

    // Create form fields
    this.createTitleField();
    this.createDescriptionField();
    this.createExpenseTypeField();
    this.createAmountCurrencyFields();
    this.createFrequencyField();
    // Skip attachments field in edit mode - edit-request-view has its own
    // attachments section that properly handles existing files
    if (!this.options.isEditMode) {
      this.createAttachmentsField();
    }

    // Add fields to form
    if (this.titleField) this.formElement.appendChild(this.titleField.getElement());
    if (this.descriptionField) this.formElement.appendChild(this.descriptionField.getElement());
    if (this.expenseTypeField) this.formElement.appendChild(this.expenseTypeField.getElement());

    // Amount and currency in a row
    const amountCurrencyRow = document.createElement('div');
    amountCurrencyRow.className = 'grid grid-cols-1 md:grid-cols-2 gap-4';
    if (this.amountField) amountCurrencyRow.appendChild(this.amountField.getElement());
    if (this.currencyField) amountCurrencyRow.appendChild(this.currencyField.getElement());
    this.formElement.appendChild(amountCurrencyRow);

    if (this.frequencyField) this.formElement.appendChild(this.frequencyField.getElement());
    if (this.attachmentsField) this.formElement.appendChild(this.attachmentsField);

    // Action buttons
    const actions = this.createActionButtons();
    this.formElement.appendChild(actions);

    card.appendChild(this.formElement);
    this.container.appendChild(card);
  }

  /**
   * Create form header
   */
  private createHeader(): HTMLElement {
    const header = document.createElement('div');
    header.className = 'flex items-center gap-3';

    const iconWrapper = document.createElement('div');
    iconWrapper.className = 'w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center';
    iconWrapper.appendChild(renderIcon('fileText', { size: 'lg', className: 'text-primary' }));

    const textWrapper = document.createElement('div');
    textWrapper.innerHTML = `
      <h1 class="text-2xl font-bold text-foreground">${this.options.isEditMode ? 'Edit Request' : 'New Expenditure Request'}</h1>
      <p class="text-sm text-muted-foreground">Fill out the form below to ${this.options.isEditMode ? 'update your' : 'submit a new'} expenditure request</p>
    `;

    header.appendChild(iconWrapper);
    header.appendChild(textWrapper);

    return header;
  }

  /**
   * Create title field with tooltip
   */
  private createTitleField(): void {
    const inputId = 'request-title';

    this.titleInput = new Input({
      id: inputId,
      name: 'title',
      type: 'text',
      placeholder: 'e.g., Office supplies for Q1',
      value: this.formData.title,
      required: true,
      maxLength: 200,
    });

    const labelWithTooltip = this.createLabelWithTooltip('Title', FIELD_TOOLTIPS.title, true);

    const wrapper = document.createElement('div');
    wrapper.className = 'flex flex-col gap-1.5';
    wrapper.appendChild(labelWithTooltip);
    wrapper.appendChild(this.titleInput.getElement());

    this.titleField = new FormField(wrapper, {
      helperText: { text: 'A brief title for your request', type: 'default' },
    });
  }

  /**
   * Create description field with tooltip
   */
  private createDescriptionField(): void {
    const inputId = 'request-description';

    this.descriptionTextarea = createTextarea({
      id: inputId,
      name: 'description',
      placeholder: 'Describe the purpose and details of this expenditure...',
      value: this.formData.description,
      required: true,
      rows: 4,
      maxLength: 2000,
    });

    const labelWithTooltip = this.createLabelWithTooltip('Description', FIELD_TOOLTIPS.description, true);

    const wrapper = document.createElement('div');
    wrapper.className = 'flex flex-col gap-1.5';
    wrapper.appendChild(labelWithTooltip);
    wrapper.appendChild(this.descriptionTextarea);

    this.descriptionField = new FormField(wrapper, {
      helperText: { text: 'Provide details about why this expenditure is needed', type: 'default' },
    });
  }

  /**
   * Create expense type dropdown with tooltip
   */
  private createExpenseTypeField(): void {
    const inputId = 'request-expense-type';

    this.expenseTypeSelect = new Select({
      id: inputId,
      name: 'expenseType',
      options: EXPENSE_TYPES,
      placeholder: 'Select expense type',
      value: this.formData.expenseType,
      required: true,
    });

    const labelWithTooltip = this.createLabelWithTooltip('Expense Type', FIELD_TOOLTIPS.expenseType, true);

    const wrapper = document.createElement('div');
    wrapper.className = 'flex flex-col gap-1.5';
    wrapper.appendChild(labelWithTooltip);
    wrapper.appendChild(this.expenseTypeSelect.getElement());

    this.expenseTypeField = new FormField(wrapper, {
      helperText: { text: 'Category for reporting and approval routing', type: 'default' },
    });
  }

  /**
   * Create amount and currency fields
   */
  private createAmountCurrencyFields(): void {
    // Amount field
    const amountId = 'request-amount';

    this.amountInput = new Input({
      id: amountId,
      name: 'amount',
      type: 'number',
      placeholder: '0.00',
      value: this.formData.amount,
      required: true,
      min: 0.01,
      step: 0.01,
    });

    const amountLabel = this.createLabelWithTooltip('Amount', FIELD_TOOLTIPS.amount, true);

    const amountWrapper = document.createElement('div');
    amountWrapper.className = 'flex flex-col gap-1.5';
    amountWrapper.appendChild(amountLabel);
    amountWrapper.appendChild(this.amountInput.getElement());

    // Add conversion preview container
    this.conversionPreview = document.createElement('div');
    this.conversionPreview.className = 'text-xs text-gray-500 dark:text-gray-400 mt-1 hidden';
    this.conversionPreview.setAttribute('aria-live', 'polite');
    amountWrapper.appendChild(this.conversionPreview);

    this.amountField = new FormField(amountWrapper, {
      helperText: { text: 'Total amount requested', type: 'default' },
    });

    // Currency field
    const currencyId = 'request-currency';

    const currencyOptions: SelectOption[] = this.currencies.map(c => ({
      value: c.code,
      label: `${c.code} (${c.symbol})`,
    }));

    this.currencySelect = new Select({
      id: currencyId,
      name: 'currency',
      options: currencyOptions,
      placeholder: 'Select currency',
      value: this.formData.currency,
      required: true,
    });

    const currencyLabel = this.createLabelWithTooltip('Currency', FIELD_TOOLTIPS.currency, true);

    const currencyWrapper = document.createElement('div');
    currencyWrapper.className = 'flex flex-col gap-1.5';
    currencyWrapper.appendChild(currencyLabel);
    currencyWrapper.appendChild(this.currencySelect.getElement());

    this.currencyField = new FormField(currencyWrapper, {
      helperText: { text: 'Currency for this expenditure', type: 'default' },
    });
  }

  /**
   * Create frequency selector with tooltip
   */
  private createFrequencyField(): void {
    const inputId = 'request-frequency';

    this.frequencySelect = new Select({
      id: inputId,
      name: 'frequency',
      options: FREQUENCY_OPTIONS,
      placeholder: 'Select frequency',
      value: this.formData.frequency || 'ONE_TIME',
      required: true,
    });

    const labelWithTooltip = this.createLabelWithTooltip('Frequency', FIELD_TOOLTIPS.frequency, true);

    const wrapper = document.createElement('div');
    wrapper.className = 'flex flex-col gap-1.5';
    wrapper.appendChild(labelWithTooltip);
    wrapper.appendChild(this.frequencySelect.getElement());

    this.frequencyField = new FormField(wrapper, {
      helperText: { text: 'One-time or recurring expense', type: 'default' },
    });
  }

  /**
   * Create attachments field with file upload component
   */
  private createAttachmentsField(): void {
    const wrapper = document.createElement('div');
    wrapper.className = 'flex flex-col gap-1.5';

    // Label with tooltip
    const labelWithTooltip = this.createLabelWithTooltip('Attachments', FIELD_TOOLTIPS.attachments, false);
    wrapper.appendChild(labelWithTooltip);

    // Info message about upload timing
    const infoMessage = document.createElement('div');
    infoMessage.className = 'flex items-center gap-2 p-3 rounded-lg bg-primary/10 border border-primary/20 text-sm text-primary mb-2';
    infoMessage.innerHTML = `
      ${renderIcon('info', { size: 'sm' }).outerHTML}
      <span>Files will be uploaded after the request is created.</span>
    `;
    wrapper.appendChild(infoMessage);

    // Create file upload container
    const fileUploadContainer = document.createElement('div');
    fileUploadContainer.id = 'file-upload-container';

    // Create file upload component with custom upload function to capture files
    this.fileUpload = new FileUpload({
      container: fileUploadContainer,
      autoUpload: true, // We use autoUpload with a custom function to capture files
      helperText: 'Supported: PDF, DOC, XLS, images (max 5MB per file)',
      uploadFn: (file, onProgress) => this.queueFile(file, onProgress),
      onChange: (files) => {
        this.handleFilesChange(files);
      },
      onRemove: (file) => {
        // Remove from queued files when removed from the UI
        // Use attachmentId as that's the key used when storing in queuedFiles map
        if (file.attachmentId) {
          this.queuedFiles.delete(file.attachmentId);
        }
      },
    });

    wrapper.appendChild(fileUploadContainer);
    this.attachmentsField = wrapper;
  }

  /**
   * Queue a file for later upload (custom uploadFn for FileUpload)
   * Simulates an upload while actually storing the file for later
   */
  private queueFile(file: File, onProgress: (progress: number) => void): Promise<{ attachmentId: string }> {
    return new Promise((resolve) => {
      // Generate a local ID for this file
      const localId = `queued-${Math.random().toString(36).substr(2, 9)}`;

      // Store the actual File object for later upload
      this.queuedFiles.set(localId, file);

      // Simulate progress
      let progress = 0;
      const interval = setInterval(() => {
        progress += 20;
        onProgress(progress);

        if (progress >= 100) {
          clearInterval(interval);
          // Return the local ID as a pseudo-attachmentId
          // The actual upload will happen when the request is created
          resolve({ attachmentId: localId });
        }
      }, 50);
    });
  }

  /**
   * Handle changes to the file queue
   */
  private handleFilesChange(_files: UploadedFile[]): void {
    // Files changed - could add additional handling here if needed
  }

  /**
   * Get queued files for upload
   */
  getQueuedFiles(): QueuedFile[] {
    const files: QueuedFile[] = [];
    const uploadedFiles = this.fileUpload?.getFiles() || [];

    for (const uploadedFile of uploadedFiles) {
      // Only include completed files (which are queued files in our case)
      if (uploadedFile.status === 'completed' && uploadedFile.attachmentId) {
        const actualFile = this.queuedFiles.get(uploadedFile.attachmentId);
        if (actualFile) {
          files.push({
            file: actualFile,
            localId: uploadedFile.attachmentId,
            name: uploadedFile.name,
            size: uploadedFile.size,
            type: uploadedFile.type,
          });
        }
      }
    }

    return files;
  }

  /**
   * Create label with tooltip icon
   */
  private createLabelWithTooltip(text: string, tooltip: string, required = false): HTMLElement {
    const labelContainer = document.createElement('div');
    labelContainer.className = 'flex items-center gap-2';

    const label = document.createElement('label');
    label.className = 'text-sm font-medium text-foreground';
    label.textContent = text;

    if (required) {
      const asterisk = document.createElement('span');
      asterisk.className = 'text-destructive ml-0.5';
      asterisk.textContent = '*';
      label.appendChild(asterisk);
    }

    const tooltipButton = document.createElement('button');
    tooltipButton.type = 'button';
    tooltipButton.className = 'text-muted-foreground hover:text-foreground transition-colors relative group';
    tooltipButton.appendChild(renderIcon('info', { size: 'xs' }));

    // Tooltip popup
    const tooltipPopup = document.createElement('div');
    tooltipPopup.className = 'absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 w-64 p-3 text-xs text-foreground bg-neutral-900 border border-neutral-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200';
    tooltipPopup.textContent = tooltip;
    tooltipButton.appendChild(tooltipPopup);

    labelContainer.appendChild(label);
    labelContainer.appendChild(tooltipButton);

    return labelContainer;
  }

  /**
   * Create action buttons
   */
  private createActionButtons(): HTMLElement {
    const actionsContainer = document.createElement('div');
    actionsContainer.className = 'flex flex-col sm:flex-row items-center justify-end gap-3 pt-4 border-t border-neutral-800';

    // Save as Draft button
    const draftBtn = document.createElement('button');
    draftBtn.type = 'button';
    draftBtn.id = 'save-draft-btn';
    draftBtn.className = 'w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground glass-button rounded-lg transition-all hover:bg-neutral-800/50';
    draftBtn.innerHTML = `
      ${renderIcon('save', { size: 'sm' }).outerHTML}
      <span>Save as Draft</span>
    `;

    // Submit button
    const submitBtn = document.createElement('button');
    submitBtn.type = 'submit';
    submitBtn.id = 'submit-btn';
    submitBtn.className = 'w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium bg-primary text-primary-foreground rounded-lg transition-all hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/25';
    submitBtn.innerHTML = `
      ${renderIcon('send', { size: 'sm' }).outerHTML}
      <span>Submit Request</span>
    `;

    actionsContainer.appendChild(draftBtn);
    actionsContainer.appendChild(submitBtn);

    return actionsContainer;
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    // Form submission
    this.formElement?.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.handleSubmit();
    });

    // Input change handlers
    this.titleInput?.on('input', (e) => {
      this.formData.title = (e.target as HTMLInputElement).value;
      this.clearFieldError('title');
    });

    this.descriptionTextarea?.addEventListener('input', (e) => {
      this.formData.description = (e.target as HTMLTextAreaElement).value;
      this.clearFieldError('description');
    });

    this.expenseTypeSelect?.on('change', () => {
      this.formData.expenseType = this.expenseTypeSelect?.getValue() || '';
      this.clearFieldError('expenseType');
    });

    this.amountInput?.on('input', (e) => {
      this.formData.amount = (e.target as HTMLInputElement).value;
      this.clearFieldError('amount');
      this.updateConversionPreview();
    });

    this.currencySelect?.on('change', () => {
      this.formData.currency = this.currencySelect?.getValue() || '';
      this.clearFieldError('currency');
      this.updateConversionPreview();
    });

    this.frequencySelect?.on('change', () => {
      this.formData.frequency = this.frequencySelect?.getValue() || '';
      this.clearFieldError('frequency');
    });

    // Save as Draft button
    const draftBtn = this.container.querySelector('#save-draft-btn');
    draftBtn?.addEventListener('click', async () => {
      await this.handleSaveDraft();
    });
  }

  /**
   * Validate form data
   */
  private validate(): boolean {
    this.errors = {};
    let isValid = true;

    // Title validation
    if (!this.formData.title.trim()) {
      this.errors.title = VALIDATION_MESSAGES.title.required;
      isValid = false;
    } else if (this.formData.title.trim().length < 3) {
      this.errors.title = VALIDATION_MESSAGES.title.minLength;
      isValid = false;
    } else if (this.formData.title.length > 200) {
      this.errors.title = VALIDATION_MESSAGES.title.maxLength;
      isValid = false;
    }

    // Description validation
    if (!this.formData.description.trim()) {
      this.errors.description = VALIDATION_MESSAGES.description.required;
      isValid = false;
    } else if (this.formData.description.trim().length < 10) {
      this.errors.description = VALIDATION_MESSAGES.description.minLength;
      isValid = false;
    } else if (this.formData.description.length > 2000) {
      this.errors.description = VALIDATION_MESSAGES.description.maxLength;
      isValid = false;
    }

    // Expense type validation
    if (!this.formData.expenseType) {
      this.errors.expenseType = VALIDATION_MESSAGES.expenseType.required;
      isValid = false;
    }

    // Amount validation
    if (!this.formData.amount) {
      this.errors.amount = VALIDATION_MESSAGES.amount.required;
      isValid = false;
    } else {
      const amount = parseFloat(this.formData.amount);
      if (isNaN(amount)) {
        this.errors.amount = VALIDATION_MESSAGES.amount.invalid;
        isValid = false;
      } else if (amount <= 0) {
        this.errors.amount = VALIDATION_MESSAGES.amount.positive;
        isValid = false;
      }
    }

    // Currency validation
    if (!this.formData.currency) {
      this.errors.currency = VALIDATION_MESSAGES.currency.required;
      isValid = false;
    }

    // Frequency validation
    if (!this.formData.frequency) {
      this.errors.frequency = VALIDATION_MESSAGES.frequency.required;
      isValid = false;
    }

    // Update UI with errors
    this.displayErrors();

    return isValid;
  }

  /**
   * Display validation errors in UI
   */
  private displayErrors(): void {
    // Title
    if (this.errors.title) {
      this.titleField?.setError(this.errors.title);
      this.titleInput?.setValidationState('error');
    } else {
      this.titleField?.clearValidation();
      this.titleInput?.setValidationState('default');
    }

    // Description
    if (this.errors.description) {
      this.descriptionField?.setError(this.errors.description);
      this.setTextareaValidationState('error');
    } else {
      this.descriptionField?.clearValidation();
      this.setTextareaValidationState('default');
    }

    // Expense type
    if (this.errors.expenseType) {
      this.expenseTypeField?.setError(this.errors.expenseType);
      this.expenseTypeSelect?.setValidationState('error');
    } else {
      this.expenseTypeField?.clearValidation();
      this.expenseTypeSelect?.setValidationState('default');
    }

    // Amount
    if (this.errors.amount) {
      this.amountField?.setError(this.errors.amount);
      this.amountInput?.setValidationState('error');
    } else {
      this.amountField?.clearValidation();
      this.amountInput?.setValidationState('default');
    }

    // Currency
    if (this.errors.currency) {
      this.currencyField?.setError(this.errors.currency);
      this.currencySelect?.setValidationState('error');
    } else {
      this.currencyField?.clearValidation();
      this.currencySelect?.setValidationState('default');
    }

    // Frequency
    if (this.errors.frequency) {
      this.frequencyField?.setError(this.errors.frequency);
      this.frequencySelect?.setValidationState('error');
    } else {
      this.frequencyField?.clearValidation();
      this.frequencySelect?.setValidationState('default');
    }
  }

  /**
   * Set validation state for textarea (manual since it's not using the class)
   */
  private setTextareaValidationState(state: ValidationState): void {
    if (!this.descriptionTextarea) return;

    // Remove all state classes
    const states = ['default', 'error', 'success', 'warning'];
    states.forEach(s => {
      this.descriptionTextarea?.classList.remove(`border-${s === 'default' ? 'neutral-800' : s === 'error' ? 'destructive' : s}`);
    });

    // Add appropriate classes based on state
    if (state === 'error') {
      this.descriptionTextarea.classList.remove('border-neutral-800');
      this.descriptionTextarea.classList.add('border-destructive');
    } else {
      this.descriptionTextarea.classList.remove('border-destructive');
      this.descriptionTextarea.classList.add('border-neutral-800');
    }
  }

  /**
   * Update currency conversion preview
   */
  private debounceTimer: number | null = null;

  private updateConversionPreview(): void {
    // Clear previous timer if exists
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }

    // Debounce the conversion calculation
    this.debounceTimer = window.setTimeout(async () => {
      if (!this.conversionPreview) return;

      const amount = parseFloat(this.formData.amount);
      const currency = this.formData.currency;

      // Hide preview if amount is invalid or currency not selected
      if (!amount || amount <= 0 || !currency) {
        this.conversionPreview.classList.add('hidden');
        return;
      }

      try {
        // Get the default currency
        const defaultCurrency = this.currencies.find(c => c.isDefault);

        // Only show conversion if not already in default currency
        if (!defaultCurrency || currency === defaultCurrency.code) {
          this.conversionPreview.classList.add('hidden');
          return;
        }

        // Perform conversion
        const result = await convertCurrencyLocal(amount, currency, defaultCurrency.code);

        // Format the converted amount
        const formattedAmount = formatCurrencyAmount(result.convertedAmount, defaultCurrency.code);

        // Update preview text
        this.conversionPreview.innerHTML = `
          <span class="flex items-center gap-1">
            ${renderIcon('trendingUp', { size: 'xs' }).outerHTML}
            Approximately ${formattedAmount} in ${defaultCurrency.name}
          </span>
        `;
        this.conversionPreview.classList.remove('hidden');
      } catch (error) {
        // Hide preview on error
        console.error('Error updating conversion preview:', error);
        this.conversionPreview.classList.add('hidden');
      }
    }, 300); // 300ms debounce
  }

  /**
   * Clear a specific field's error
   */
  private clearFieldError(field: keyof RequestFormData): void {
    delete this.errors[field];

    switch (field) {
      case 'title':
        this.titleField?.clearValidation();
        this.titleInput?.setValidationState('default');
        break;
      case 'description':
        this.descriptionField?.clearValidation();
        this.setTextareaValidationState('default');
        break;
      case 'expenseType':
        this.expenseTypeField?.clearValidation();
        this.expenseTypeSelect?.setValidationState('default');
        break;
      case 'amount':
        this.amountField?.clearValidation();
        this.amountInput?.setValidationState('default');
        break;
      case 'currency':
        this.currencyField?.clearValidation();
        this.currencySelect?.setValidationState('default');
        break;
      case 'frequency':
        this.frequencyField?.clearValidation();
        this.frequencySelect?.setValidationState('default');
        break;
    }
  }

  /**
   * Handle form submission with confirmation
   */
  private async handleSubmit(): Promise<void> {
    // Validate first
    if (!this.validate()) {
      // Scroll to first error (check if scrollIntoView exists for test environments)
      const firstError = this.container.querySelector('.border-destructive') as HTMLElement | null;
      if (firstError && typeof firstError.scrollIntoView === 'function') {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Show confirmation dialog
    const expenseTypeLabel = EXPENSE_TYPES.find(t => t.value === this.formData.expenseType)?.label || 'Unknown';
    const currency = this.currencies.find(c => c.code === this.formData.currency);
    const currencySymbol = currency?.symbol || this.formData.currency;
    const amount = parseFloat(this.formData.amount).toFixed(2);

    const confirmed = await confirm({
      title: 'Submit Request',
      message: `Are you sure you want to submit this expenditure request?\n\n• Title: ${this.formData.title}\n• Amount: ${currencySymbol} ${amount}\n• Type: ${expenseTypeLabel}\n\nOnce submitted, the request will be sent for approval and cannot be edited.`,
      confirmText: 'Submit Request',
      cancelText: 'Cancel',
      icon: 'send',
    });

    if (!confirmed) return;

    // Submit the form
    this.setSubmitting(true);

    try {
      if (this.options.onSubmit) {
        // Use getFormData() to include queued files for upload
        await this.options.onSubmit(this.getFormData());
      }
    } catch (error) {
      console.error('Submit error:', error);
      // Error handling is done in the callback
    } finally {
      this.setSubmitting(false);
    }
  }

  /**
   * Handle save as draft
   */
  private async handleSaveDraft(): Promise<void> {
    // Basic validation for draft (at least title)
    if (!this.formData.title.trim()) {
      this.errors.title = 'Please enter a title to save as draft';
      this.displayErrors();
      this.titleInput?.focus();
      return;
    }

    this.setSavingDraft(true);

    try {
      if (this.options.onSaveDraft) {
        // Use getFormData() to include queued files for upload
        await this.options.onSaveDraft(this.getFormData());
      }
    } catch (error) {
      console.error('Save draft error:', error);
      // Error handling is done in the callback
    } finally {
      this.setSavingDraft(false);
    }
  }

  /**
   * Set submitting state
   */
  private setSubmitting(submitting: boolean): void {
    const submitBtn = this.container.querySelector('#submit-btn') as HTMLButtonElement;
    const draftBtn = this.container.querySelector('#save-draft-btn') as HTMLButtonElement;

    if (submitBtn) {
      submitBtn.disabled = submitting;
      submitBtn.innerHTML = submitting
        ? `${renderIcon('loader', { size: 'sm', className: 'animate-spin' }).outerHTML}<span>Submitting...</span>`
        : `${renderIcon('send', { size: 'sm' }).outerHTML}<span>Submit Request</span>`;
    }

    if (draftBtn) {
      draftBtn.disabled = submitting;
    }

    // Disable form inputs while submitting
    this.setFormDisabled(submitting);
  }

  /**
   * Set saving draft state
   */
  private setSavingDraft(saving: boolean): void {
    const submitBtn = this.container.querySelector('#submit-btn') as HTMLButtonElement;
    const draftBtn = this.container.querySelector('#save-draft-btn') as HTMLButtonElement;

    if (draftBtn) {
      draftBtn.disabled = saving;
      draftBtn.innerHTML = saving
        ? `${renderIcon('loader', { size: 'sm', className: 'animate-spin' }).outerHTML}<span>Saving...</span>`
        : `${renderIcon('save', { size: 'sm' }).outerHTML}<span>Save as Draft</span>`;
    }

    if (submitBtn) {
      submitBtn.disabled = saving;
    }

    // Disable form inputs while saving
    this.setFormDisabled(saving);
  }

  /**
   * Disable/enable form inputs
   */
  private setFormDisabled(disabled: boolean): void {
    this.titleInput?.setDisabled(disabled);
    if (this.descriptionTextarea) {
      this.descriptionTextarea.disabled = disabled;
    }
    this.expenseTypeSelect?.setDisabled(disabled);
    this.amountInput?.setDisabled(disabled);
    this.currencySelect?.setDisabled(disabled);
    this.frequencySelect?.setDisabled(disabled);
    this.fileUpload?.setDisabled(disabled);
  }

  /**
   * Get current form data including queued files
   */
  getFormData(): RequestFormData {
    return {
      ...this.formData,
      queuedFiles: this.getQueuedFiles(),
    };
  }

  /**
   * Set form data (for editing)
   */
  setFormData(data: Partial<RequestFormData>): void {
    this.formData = { ...this.formData, ...data };

    // Update input values
    if (this.titleInput && data.title !== undefined) {
      this.titleInput.setValue(data.title);
    }
    if (this.descriptionTextarea && data.description !== undefined) {
      this.descriptionTextarea.value = data.description;
    }
    if (this.expenseTypeSelect && data.expenseType !== undefined) {
      this.expenseTypeSelect.setValue(data.expenseType);
    }
    if (this.amountInput && data.amount !== undefined) {
      this.amountInput.setValue(data.amount);
    }
    if (this.currencySelect && data.currency !== undefined) {
      this.currencySelect.setValue(data.currency);
    }
    if (this.frequencySelect && data.frequency !== undefined) {
      this.frequencySelect.setValue(data.frequency);
    }
  }

  /**
   * Reset form to initial state
   */
  reset(): void {
    this.formData = {
      title: '',
      description: '',
      expenseType: '',
      amount: '',
      currency: this.currencies.find(c => c.isDefault)?.code || 'CHF',
      frequency: 'ONE_TIME',
    };
    this.errors = {};

    // Reset input values
    this.titleInput?.setValue('');
    if (this.descriptionTextarea) {
      this.descriptionTextarea.value = '';
    }
    this.expenseTypeSelect?.setValue('');
    this.amountInput?.setValue('');
    this.currencySelect?.setValue(this.formData.currency);
    this.frequencySelect?.setValue('ONE_TIME');

    // Clear queued files
    this.queuedFiles.clear();
    this.fileUpload?.clear();

    // Clear validation states
    this.displayErrors();
  }

  /**
   * Destroy the component
   */
  destroy(): void {
    this.container.innerHTML = '';
  }
}

/**
 * Factory function to create and initialize a request form
 */
export async function createRequestForm(options: RequestFormOptions): Promise<RequestForm> {
  const form = new RequestForm(options);
  await form.init();
  return form;
}
